import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPricingData, storeCrmPricing } from '../utils/api';
import { initPhonePe, createOrder, checkoutWithPhonePe } from '../utils/payment';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ClipLoader } from "react-spinners";
import { initializePayment } from '../utils/paymentUtils';
import { PaymentType } from '../utils/paymentTypes';

export default function PricingTables() {
  const { data: pricingData = [], isLoading } = useQuery({
    queryKey: ['pricingData'],
    queryFn: fetchPricingData,
  });
  const [processingPlanKey, setProcessingPlanKey] = useState(null);

  const numberWithCommas = x =>
    x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const applyDiscount = price => Math.round(price * (1 - 0.12295));

  const handleCheckout = async (plan, idx) => {
    const key = plan.id ?? idx;
    setProcessingPlanKey(key);
    try {
      const token = localStorage.getItem('Startuptoken');
      if (!token) {
        toast.error('Please log in to continue');
        return;
      }

      // Store CRM plan data in sessionStorage
      sessionStorage.setItem('crm_plan', JSON.stringify({
        id: plan.id,
        description: plan.description,
        annual_price: applyDiscount(plan.annual_price),
        max_storage_size: plan.max_storage_size,
        max_employees: plan.max_employees
      }));

      // Initialize payment
      const paymentResult = await initializePayment({
        type: PaymentType.CRM,
        amount: applyDiscount(plan.annual_price),
        token,
        returnUrl: `${window.location.origin}/payment-callback`
      });

      toast.loading('Processing payment...', { id: 'payment' });
    } catch (error) {
      console.error('Payment Error:', error);
      toast.error(error.message || 'Payment processing failed');
    } finally {
      setProcessingPlanKey(null);
    }
  };

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="pt-32 pb-12 md:pt-12 md:pb-20 text-center">
          {pricingData.length > 0 && (
            <>  
              <h1 className="text-4xl font-bold mb-4" data-aos="zoom-y-out">
                {pricingData[0].price_title}
              </h1>
              <p
                className="text-xl text-gray-600"
                data-aos="zoom-y-out"
                data-aos-delay="150"
              >
                {pricingData[0].price_description}
              </p>
            </>
          )}
        </div>

        <div className="max-w-sm md:max-w-2xl xl:max-w-none mx-auto grid gap-8 md:grid-cols-2 xl:grid-cols-4 xl:gap-6 items-start">
          {isLoading
            ? Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="bg-white rounded shadow-xl p-6 h-64 space-y-3 animate-pulse">
                  {Array.from({ length: 6 }).map((__, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded w-full" />
                  ))}
                </div>
              ))
            : pricingData.slice(2).map((plan, index) => {
                const isEnterprise = index === 3;
                const discounted = plan.annual_price > 0 ? applyDiscount(plan.annual_price) : 0;
                const key = plan.id ?? index;
                const isProcessing = processingPlanKey === key;
                return (
                  <div
                    key={key}
                    className={`relative flex flex-col h-full py-5 px-6 rounded shadow-xl ${
                      plan.is_recommended === 1 ? 'border-2 border-blue-500 bg-blue-100' : ''
                    }`}
                    data-aos="zoom-y-out"
                    data-aos-delay="450"
                  >
                    {plan.is_recommended === 1 && (
                      <div className="absolute top-0 right-0 mr-5 px-3 py-2 -mt-5 bg-yellow-500 rounded-full">
                        ★
                      </div>
                    )}

                    <div className="mb-4">
                      <div className="text-lg font-bold mb-1">{plan.description}</div>

                      <div className="inline-flex items-baseline mb-2 space-x-2">
                        {isEnterprise ? (
                          <div className="flex items-center justify-center p-2 rounded-full bg-gray-200">
                            <img
                              src="/phone.svg"
                              alt="Contact Us"
                              className="w-6 object-contain"
                            />
                          </div>
                        ) : (
                          <>
                            <span className="text-3xl font-bold">₹{numberWithCommas(discounted)}</span>
                            <span className="text-gray-600">/year</span>
                          </>
                        )}
                      </div>
                    </div>

                    <ul className="text-gray-600 -mb-2 space-y-2">
                      <li className="flex items-center">✓ {isEnterprise ? '∞ Storage Size' : `${plan.max_storage_size}GB Storage Size`}</li>
                      <li className="flex items-center">✓ {isEnterprise ? '∞ Employees' : `Up to ${plan.max_employees} employees`}</li>
                      <li className="flex items-center">✓ 24/7 Support</li>
                      <li className="flex items-center">✓ {isEnterprise ? 'Business Customization' : 'Website Deployment'}</li>
                    </ul>

                    <div className="border-t border-gray-200 pt-5 mt-6">
                      {isEnterprise ? (
                        <Link to="/contact-us">
                          <button className="btn-sm text-white bg-red-600 hover:bg-rose-700 w-full py-2 rounded">CONTACT US</button>
                        </Link>
                      ) : (
                        <button
                          className="btn-sm text-white bg-blue-600 hover:bg-blue-700 w-full py-2 rounded flex items-center justify-center"
                          onClick={() => handleCheckout(plan, index)}
                          disabled={isProcessing}
                        >
                          {isProcessing && (
                            <ClipLoader size={18} />
                          )}
                          {isProcessing ? 'Processing...' : 'GET STARTED'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
      </div>
    </section>
  );
}
